// @ts-nocheck
import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { App as CapApp } from '@capacitor/app';
import { registerPushNotifications, unregisterPushNotifications } from '../lib/pushRegistration';

interface MobileAuthState {
  user: User | null;
  session: Session | null;
  profile: {
    id: string;
    email?: string;
    name?: string;
  } | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
}

/**
 * Simple mobile auth hook - NO role-based redirects
 * Works for ALL user types (athletes, coaches, parents, etc.)
 */
export function useMobileAuth(): MobileAuthState {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<{ id: string; email?: string; name?: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('[MobileAuth] Auth state changed:', event);
        setSession(session);
        setUser(session?.user ?? null);
        
        // Fetch profile after auth state change
        if (session?.user) {
          setTimeout(() => {
            fetchProfile(session.user.id, session.user.email);
            // Ask for push permission on the screen the user lands on after sign-in.
            if (event === 'SIGNED_IN') {
              registerPushNotifications({ prompt: true });
            }
          }, 0);
        } else {
          setProfile(null);
          setLoading(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchProfile(session.user.id, session.user.email);
        // Existing session at launch: refresh the token, but never prompt here.
        registerPushNotifications();
      } else {
        setLoading(false);
      }
    });

    // Apple reissues tokens: refresh whenever the app comes back to the front.
    let resumeHandle: { remove: () => void } | null = null;
    CapApp.addListener('resume', () => {
      registerPushNotifications();
    })
      .then((h) => {
        resumeHandle = h;
      })
      .catch(() => {});

    return () => {
      subscription.unsubscribe();
      resumeHandle?.remove();
    };
  }, []);

  const fetchProfile = async (userId: string, userEmail?: string) => {
    console.log('[MobileAuth] Fetching profile for auth user:', userId);
    try {
      // Query by both user_id OR email to handle profile linking variations
      let query = supabase
        .from('profiles')
        .select('id, email, name');
      
      if (userEmail) {
        query = query.or(`user_id.eq.${userId},email.eq.${userEmail}`);
      } else {
        query = query.eq('id', userId);
      }
      
      const { data, error } = await query.maybeSingle();

      if (error) {
        console.error('[MobileAuth] Error fetching profile:', error);
      } else if (data) {
        setProfile(data);
      } else {
        console.log('[MobileAuth] No profile found for user:', userId, userEmail);
        // Still allow user to proceed without profile
        setProfile(null);
      }
    } catch (err) {
      console.error('[MobileAuth] Exception fetching profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || 'An unexpected error occurred' };
    }
  };

  const signOut = async () => {
    // Remove this person's token before the session goes away.
    await unregisterPushNotifications();
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
  };

  return {
    user,
    session,
    profile,
    loading,
    signIn,
    signOut,
  };
}